import CalendarEventPeekCallout from './CalendarEventPeekCallout';
import * as styles from './MonthViewDayAgendaCallout.scss';
import { calendarAssetNewEventButtonText } from '../calendar.locstring.json';
import {
    AgendaScenarioId,
    MonthViewDayAgendaCalloutMaxHeight,
    MonthViewDayAgendaCalloutWidth,
} from '../constants';
import { useCalendarEventInteractionCallbacks } from '../hooks/useCalendarEventInteractionCallbacks';
import useMonthViewDayAgendaCalloutState from '../hooks/useMonthViewDayAgendaCalloutState';
import type { CalendarViewBaseProps } from '../schema';
import { convertToOwaDate } from '../utils/dateUtils';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { DirectionalHint, FocusTrapCallout } from '@fluentui/react/lib/Callout';
import { observer } from 'mobx-react-lite';
import { AgendaEventsView } from 'owa-agenda-events-list';
import { formatWeekDayMonthDayYear } from 'owa-datetime';
import loc from 'owa-localize';
import * as React from 'react';

export interface MonthViewDayAgendaCalloutProps extends CalendarViewBaseProps {
    target: HTMLElement | undefined;
    calendarIds: string[];
    date: Date | undefined;
    onCalloutDismiss: () => void;
    isAgendaVisible: boolean;
    setIsAgendaVisible: (value: boolean) => void;
}

export default observer(function MonthViewDayAgendaCallout(props: MonthViewDayAgendaCalloutProps) {
    const {
        onOpenEventDetails,
        target,
        date,
        onCalloutDismiss,
        onContextMenuInvoked,
        onClickCalendarEvent,
        peekTarget,
        onEventsPeekCalloutDismissed,
        isAgendaVisible,
    } = props;

    const {
        onWheel,
        showNewEventButton,
        onNewEventButtonClick,
    } = useMonthViewDayAgendaCalloutState(props);

    const calendarEventInteractionCallbacks = useCalendarEventInteractionCallbacks(
        onOpenEventDetails,
        date,
        onClickCalendarEvent,
        onContextMenuInvoked
    );

    const owaDate = date ? convertToOwaDate(date) : undefined;

    const focusTrapProps = {
        style: { maxHeight: MonthViewDayAgendaCalloutMaxHeight },
        className: styles.monthAgendaFocusTrap,
        isClickableOutsideFocusTrap: true,
        forceFocusInsideTrap: false,
    };

    return !peekTarget ? (
        <FocusTrapCallout
            hidden={!target || !date || !isAgendaVisible}
            target={target}
            directionalHint={DirectionalHint.rightCenter}
            setInitialFocus={true}
            onWheel={onWheel}
            onDismiss={onCalloutDismiss}
            calloutMaxHeight={MonthViewDayAgendaCalloutMaxHeight}
            calloutWidth={MonthViewDayAgendaCalloutWidth}
            focusTrapProps={focusTrapProps}>
            <div className={styles.monthAgendaContainer}>
                <div className={styles.dateHeader}>
                    {owaDate ? formatWeekDayMonthDayYear(owaDate) : undefined}
                </div>
                <AgendaEventsView
                    scenarioId={AgendaScenarioId}
                    callbacks={{
                        calendarEventInteractionCallback: calendarEventInteractionCallbacks,
                    }}
                />
            </div>
            {showNewEventButton && (
                <div className={styles.newEventContainer}>
                    <DefaultButton
                        text={loc(calendarAssetNewEventButtonText)}
                        className={styles.newEventButton}
                        onClick={onNewEventButtonClick}
                    />
                </div>
            )}
        </FocusTrapCallout>
    ) : (
        <CalendarEventPeekCallout target={target} onDismissed={onEventsPeekCalloutDismissed} />
    );
});
